import camelcaseKeys from 'camelcase-keys';
import { decodeHTML } from 'entities';
import snakecaseKeys from 'snakecase-keys';
import { z } from 'zod';

import { isPlainObject } from '../utils/type-guards';

export { IsoDateCodec, IsoDateRangeCodec, NullableIsoDateCodec } from './date-codecs';

/**
 * Standard Zod Codecs - Based on canonical implementations from zod.dev/codecs
 * These are the official patterns recommended by the Zod team
 */

// Date/Time codecs (canonical from Zod docs)
// Accept ISO datetimes with or without timezone 'Z' (allow offsets)
export const isoDatetimeToDate = z.codec(z.iso.datetime(), z.date(), {
  decode: (isoString, _payload) => new Date(isoString),
  encode: (date, _payload) => date.toISOString(),
});

// Accept either ISO string or Date and normalize to Date (boundary-friendly)
export const isoOrDateToDate = z.codec(z.union([z.iso.datetime(), z.date()]), z.date(), {
  decode: (value, _payload) => (typeof value === 'string' ? new Date(value) : value),
  encode: (date, _payload) => date.toISOString(),
});

// Nullable version for optional dates
export const nullableIsoDatetimeToDate = z.codec(z.union([z.iso.datetime(), z.date()]).nullish(), z.date().nullable(), {
  decode: (value, _payload) => {
    if (value === undefined) {
      return null;
    }
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    if (isDateObject(value)) {
      const timestamp = value.getTime();
      if (Number.isNaN(timestamp)) {
        return null;
      }
      return value;
    }
    return null;
  },
  encode: (date) => (date ? date.toISOString() : null),
});

// =========================================================
// Common Response Patterns with Codecs
// =========================================================

/**
 * Reusable paginated response pattern
 * Automatically includes timestamp with codec conversion
 */
export const createPaginatedSchema = <T extends z.ZodType>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    pagination: z.object({
      cursor: z.string().nullable(),
      hasMore: z.boolean(),
      page: z.number().optional(),
      pageSize: z.number().optional(),
      totalCount: z.number().nullable().optional(),
    }),
    timestamp: isoDatetimeToDate, // When the response was generated
  });

export const json = <T extends z.ZodType>(schema: T) =>
  z.codec(z.string(), schema, {
    decode: (string_, _payload) => {
      try {
        const parsed: unknown = JSON.parse(string_);
        // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
        return parsed as z.input<T>;
      } catch {
        throw new z.ZodError([
          {
            code: 'custom',
            message: 'Invalid JSON payload',
            path: [],
          },
        ]);
      }
    },
    encode: (object, _payload) => JSON.stringify(object),
  });

/**
 * Convert nullable inputs to optional outputs while preserving validation
 * at the boundary using an inner schema. This is useful when an API encodes
 * "missing" values as null, but the application uses undefined for absence.
 *
 * Example:
 *   nullishToOptional(z.string()) // string | null | undefined -> string | undefined
 *   nullishToOptional(z.url()) // preserves URL validation
 */
export const nullishToOptional = <T extends z.ZodType>(inner: T) =>
  z.preprocess((v) => (v === undefined ? undefined : v), inner.optional());

/**
 * HTML Entities → Text codec
 *
 * Decodes a safe, minimal set of HTML entities commonly found in text fields
 * coming from external sources that may pre-escape content (e.g., &lt;, &gt;, &amp;,
 * &quot;, &#39;, plus numeric references like &#x3C; and &#60;).
 *
 * Intent: Normalize external text to a single internal representation so the
 * renderer can escape once and display characters like '<' and '>' correctly.
 */
export const htmlEntitiesToText = z.codec(z.string(), z.string(), {
  decode: (input, _payload) => {
    if (!input || typeof input !== 'string') {
      return '';
    }
    return decodeHTML(input);
  },
  encode: (text, _payload) => text,
});

// Normalize any nested Date instances to ISO strings inside JSON-like structures
function normalizeJsonDatesToIso(input: unknown): unknown {
  if (isDateObject(input)) return input.toISOString();
  if (Array.isArray(input)) return input.map((value) => normalizeJsonDatesToIso(value));
  if (isPlainObject(input)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      out[k] = normalizeJsonDatesToIso(v);
    }
    return out;
  }
  return input;
}

export const createJsonbCodec = <T extends z.ZodType>(schema: T) =>
  z.codec(z.unknown(), schema, {
    decode: (value, _payload) => {
      if (value === null || value === undefined) {
        // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
        return value as z.input<T>;
      }

      let parsed: unknown = value;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          throw new z.ZodError([
            {
              code: 'custom',
              path: [],
              message: 'Invalid JSONB payload: failed to parse stringified value.',
            },
          ]);
        }
      }

      // Ensure single input format: Dates → ISO strings before schema parsing.
      const normalized = normalizeJsonDatesToIso(parsed);
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      return normalized as z.input<T>;
    },
    encode: (object, _payload) => normalizeJsonDatesToIso(object),
  });

/**
 * Nullable JSONB codec - allows null values to pass through
 * Eliminates defensive z.unknown().transform() patterns for JSONB fields
 */
export const createNullableJsonbCodec = <T extends z.ZodType>(schema: T) =>
  z.codec(z.unknown(), schema.nullable(), {
    decode: (value, _payload) => {
      if (value === null || value === undefined) {
        return null;
      }
      if (typeof value === 'string') {
        try {
          const parsed: unknown = JSON.parse(value);
          // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
          const candidate = parsed as z.input<T>;
          const result = schema.safeParse(candidate);
          return result.success ? candidate : null;
        } catch {
          return null;
        }
      }
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      const candidate = value as z.input<T>;
      const result = schema.safeParse(candidate);
      return result.success ? candidate : null;
    },
    encode: (object, _payload) => object,
  });

/**
 * JSONB codec that also normalizes snake_case keys to camelCase on decode.
 * Useful for reading JSONB from DB where keys may be stored in snake_case
 * while the application expects camelCase + proper types (e.g., Date via codecs).
 */
export const createJsonbSnakeToCamelCodec = <T extends z.ZodType>(schema: T) =>
  z.codec(z.unknown(), schema, {
    decode: (value, _payload) => {
      let parsed: unknown = value;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch {
          parsed = {};
        }
      }
      const safeInput = isPlainObject(parsed) ? parsed : {};
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      return camelcaseKeys(safeInput, { deep: true }) as z.input<T>;
    },
    encode: (object, _payload) => (isPlainObject(object) ? snakecaseKeys(object, { deep: true }) : object),
  });

type CamelCaseConvertible = Record<string, unknown> | readonly Record<string, unknown>[];
type SnakeCaseConvertible = Record<string, unknown> | readonly Record<string, unknown>[];

const transformKeysDeep = (value: unknown, transform: (key: string) => string): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => transformKeysDeep(item, transform));
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value)) {
      result[transform(key)] = transformKeysDeep(nested, transform);
    }
    return result;
  }

  return value;
};

const SNAKE_TO_CAMEL_REGEX = /_([a-z0-9])/g;
const CAMEL_TO_SNAKE_REGEX = /([A-Z])/g;
const LEADING_UNDERSCORE_REGEX = /^_/;

const snakeKeyToCamel = (key: string): string =>
  key.replaceAll(SNAKE_TO_CAMEL_REGEX, (_, char: string) => char.toUpperCase());

const camelKeyToSnake = (key: string): string =>
  key.replaceAll(CAMEL_TO_SNAKE_REGEX, '_$1').replace(LEADING_UNDERSCORE_REGEX, '').toLowerCase();

// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
export const snakeToCamel = <T extends CamelCaseConvertible>(value: T): T =>
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  transformKeysDeep(value, snakeKeyToCamel) as T;

// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
export const camelToSnake = <T extends SnakeCaseConvertible>(value: T): T =>
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  transformKeysDeep(value, camelKeyToSnake) as T;

// =========================================================
// Misc. Boundary Codecs
// =========================================================

// ----------------------------------------------------------------------------
// X/Twitter specific: Reply Settings
// ----------------------------------------------------------------------------

// Canonical internal enum used across the app
export const ReplySettingsEnum = z.enum([
  'everyone',
  'mentionedUsers',
  'followers',
  'subscribers',
  'verified',
  'other',
]);
export type ReplySettings = z.infer<typeof ReplySettingsEnum>;

/**
 * ReplySettingsCodec
 *
 * Accepts X API variants at the boundary and normalizes to our canonical union:
 * - 'everyone'           → 'everyone'
 * - 'mentioned_users'    → 'mentionedUsers'
 * - 'mentionedUsers'     → 'mentionedUsers'
 * - 'following'          → 'followers'   (X uses both historically; we standardize)
 * - 'followers'          → 'followers'
 * - 'subscribers'        → 'subscribers'
 * - 'verified'           → 'verified'
 * - 'other'              → 'other' (X introduced September 2025 for custom audience lists)
 *
 * Why we normalize:
 * - Documentation mismatch: X docs state values are "everyone", "mentioned_users", "followers".
 *   Live API responses observed on 2025-09-12 and 2025-09-11 returned "following", "mentionedUsers",
 *   "verified", and "subscribers" in the wild.
 * - To keep a single internal format, we normalize variants at the boundary only.
 *
 * One‑liner to verify (uses xl CLI; replace TOKEN as needed):
 *   ./xl /2/tweets/search/recent -t "<TOKEN>" -q "query=the lang:en -is:retweet -is:quote&tweet.fields=reply_settings&expansions=referenced_tweets.id&max_results=100" \
 *     | jq -r 'def docs: ["everyone","mentioned_users","followers"]; def vals: ([.data[]?.reply_settings, .includes.tweets[]?.reply_settings] | flatten | map(select(. != null)) | unique | sort); "docs: " + (docs|join(", ")) + "\napi:  " + (vals|join(", ")) + "\nextra_in_api: " + ((vals - docs)|join(", ")) + "\nmissing_from_api: " + ((docs - vals)|join(", "))'
 * Example output (2025-09-12):
 *   docs: everyone, mentioned_users, followers
 *   api:  everyone, following, mentionedUsers, subscribers, verified
 *   extra_in_api: following, mentionedUsers, subscribers, verified
 *   missing_from_api: mentioned_users, followers
 *
 * Strict for unknown values to surface upstream changes early without leaking
 * inconsistent strings into the app layer.
 */
export const ReplySettingsCodec = z.codec(z.string(), ReplySettingsEnum, {
  decode: (value, _payload) => {
    const v = value.trim();
    if (v === 'everyone') return 'everyone';
    if (v === 'mentioned_users' || v === 'mentionedUsers') return 'mentionedUsers';
    if (v === 'followers' || v === 'following') return 'followers';
    if (v === 'subscribers') return 'subscribers';
    if (v === 'verified') return 'verified';
    if (v === 'other') return 'other';
    throw new z.ZodError([
      {
        code: 'custom',
        path: [],
        message: `Invalid reply_settings '${value}'. Expected one of: ${ReplySettingsEnum.options.join(
          ', '
        )}, mentioned_users, following`,
      },
    ]);
  },
  encode: (canonical, _payload) => canonical,
});

/**
 * Accept either a plain record of string → string or an array of pairs
 * and normalize to a record. This is intended strictly for boundary inputs
 * to migrate legacy payloads without polluting internal types.
 */
export const recordFromEntriesCodec = z.codec(
  z.union([z.record(z.string(), z.string()), z.array(z.tuple([z.string(), z.string()]))]),
  z.record(z.string(), z.string()),
  {
    decode: (value, _payload) => {
      if (Array.isArray(value)) {
        const entries: [string, string][] = [];
        for (const tuple of value) {
          if (Array.isArray(tuple) && tuple.length === 2) {
            const [key, value_] = tuple;
            if (typeof key === 'string' && typeof value_ === 'string') {
              entries.push([key, value_]);
            }
          }
        }
        return Object.fromEntries(entries);
      }
      return value;
    },
    encode: (object, _payload) => object,
  }
);

// =========================================================
// UUID Codec
// =========================================================

// Canonical UUID codec to enforce RFC4122 UUIDs at boundaries
export const UuidCodec = z.codec(z.uuid(), z.uuid(), {
  decode: (s, _payload) => s,
  encode: (s, _payload) => s,
});
const isDateObject = (value: unknown): value is Date =>
  typeof value === 'object' && value !== null && Object.prototype.toString.call(value) === '[object Date]';
